<template>
  <div class="text-left" :class="{ 'is-invalid': $v.inputValue.$error }">
    <label :for="`dynamicInput-${inputData.id}`" v-html="addNbsp(getLocaleText(inputData.label))" />
    <v-text-field
      :id="`dynamicInput-${inputData.id}`"
      v-model="$v.inputValue.$model"
      solo
      flat
      :placeholder="getLocaleText(inputData.placeholder)"
      :class="[
        {
          'is-valid':
            $v.inputValue.$dirty &&
            !$v.inputValue.$invalid &&
            ((!inputData.required && inputValue.length) || inputData.required)
        },
        dependentClasses
      ]"
      :error="$v.inputValue.$error"
      :error-messages="errorMessage"
      :messages="addNbsp(getLocaleText(inputData.help))"
      hide-details="auto"
      @focus="handleFocus"
      @change="updateValue"
    >
      <template #append>
        <v-icon
          v-if="
            $v.inputValue.$dirty &&
            !$v.inputValue.$invalid &&
            ((!inputData.required && inputValue.length) || inputData.required)
          "
        >
          $tick
        </v-icon>
        <v-icon v-if="$v.inputValue.$error"> $warning</v-icon>
      </template>

      <template #message="{ message }">
        <span v-html="message" />
      </template>
    </v-text-field>
  </div>
</template>

<script>
import { phone, zipCode } from '@/customVuelidate';
import {
  email,
  required,
  between,
  maxLength,
  minLength,
  numeric,
  minValue,
  maxValue
} from 'vuelidate/lib/validators';
import formGtm from '@/mixins/formGtm';

export default {
  name: 'UlInput',
  mixins: [formGtm],
  props: {
    inputData: {
      type: Object,
      required: true
    },
    errorText: {
      type: String,
      default: ''
    },
    parentValueId: {
      type: Number,
      default: null
    },
    stepId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      inputValue: '',
      emailError: false //TODO: doriesit, tahat z API
    };
  },
  validations() {
    let validation = { inputValue: {} };
    if (this.inputData.required) {
      validation.inputValue = { required };
    }
    if (this.inputData.inputValidation === 'percent') {
      validation.inputValue = {
        ...validation.inputValue,
        minValue: minValue(0),
        maxValue: maxValue(100),
        numeric
      };
    }
    if (this.inputData.inputValidation === 'integer') {
      validation.inputValue = {
        ...validation.inputValue,
        minValue: minValue(this.inputData.minValue),
        maxValue: maxValue(this.inputData.maxValue),
        numeric
      };
    }
    if (this.inputData.inputValidation === 'monthdays') {
      validation.inputValue = {
        ...validation.inputValue,
        minValue: minValue(0),
        maxValue: maxValue(31),
        numeric
      };
    }
    if (this.inputData.inputValidation === 'yeardays') {
      validation.inputValue = {
        ...validation.inputValue,
        minValue: minValue(0),
        maxValue: maxValue(365),
        numeric
      };
    }
    if (this.inputData.inputValidation === 'email') {
      validation.inputValue = { ...validation.inputValue, email };
    }
    if (this.inputData.inputValidation === 'phonenumber') {
      validation.inputValue = { ...validation.inputValue, phone };
    }
    if (this.inputData.inputValidation === 'zip') {
      validation.inputValue = { ...validation.inputValue, zipCode };
    }
    if (this.inputData.inputValidation === 'birthyear') {
      validation.inputValue = {
        ...validation.inputValue,
        numeric,
        minLength: minLength(4),
        maxLength: maxLength(4),
        between: between(new Date().getFullYear() - 120, new Date().getFullYear())
      };
    }
    if (this.inputData.inputValidation === 'pastyear') {
      validation.inputValue = {
        ...validation.inputValue,
        numeric,
        minLength: minLength(4),
        maxLength: maxLength(4),
        between: between(new Date().getFullYear() - 120, new Date().getFullYear())
      };
    }
    return validation;
  },
  computed: {
    responses() {
      return this.$store.getters[`dynamicForms/responses`];
    },
    isDependent() {
      return (
        this.inputData.dependencyType === 'after_value' ||
        this.inputData.dependencyType === 'subquestion'
      );
    },
    dependentClasses() {
      if (this.inputData.dependencyType === 'after_value') {
        return 'dependent__after-value';
      }
      if (this.inputData.dependencyType === 'subquestion') {
        return 'dependent__subquestion';
      }
      return '';
    },
    errorMessage() {
      if (!this.$v.inputValue.$dirty) {
        //don't show error if user didn't touch input
        return '';
      }
      if (this.inputData.identifier === 'firstname') {
        if (this.$v.inputValue.$error) {
          return this.$t('questionForm.firstName.errorText');
        }
      }
      if (this.inputData.identifier === 'lastname') {
        if (this.$v.inputValue.$error) {
          return this.$t('questionForm.lastName.errorText');
        }
      }
      if (this.inputData.inputValidation === 'email') {
        if (
          this.$v.inputValue.$error &&
          this.$v.inputValue.$invalid &&
          this.$v.inputValue.required
        ) {
          return this.$t('questionForm.email.errorEmailText');
        } else if (this.emailError) {
          return this.$t('questionForm.email.errorEmailNotExisting');
        } else if (this.$v.inputValue.$error) {
          return this.$t('questionForm.email.errorText');
        }
      }
      if (this.inputData.inputValidation === 'phonenumber') {
        if (
          this.$v.inputValue.$error &&
          this.$v.inputValue.$invalid &&
          this.$v.inputValue.required
        ) {
          return this.$t('questionForm.phone.errorPhoneText', { exampleNumber: '+420123456789' });
        } else if (this.$v.inputValue.$error) {
          return this.$t('questionForm.phone.errorText');
        }
      }
      if (this.inputData.inputValidation === 'zip') {
        if (this.inputValue && !this.$v.inputValue.zipCode && this.inputValue.length >= 1) {
          return this.$t('questionForm.zipCode.errorZipCodeText');
        } else if (this.$v.inputValue.$error) {
          return this.$t('questionForm.zipCode.errorText');
        }
      }
      if (this.inputData.inputValidation === 'birthyear') {
        if (
          !this.$v.inputValue.numeric ||
          !this.$v.inputValue.minLength ||
          !this.$v.inputValue.maxLength
        ) {
          return this.$t('questionForm.birthYear.errorLengthText');
        } else if (!this.$v.inputValue.between) {
          return this.$t('questionForm.birthYear.errorBetweenText');
        } else if (this.$v.inputValue.$error) {
          return this.$t('questionForm.birthYear.errorText');
        }
      }
      if (this.inputData.inputValidation === 'pastyear') {
        if (
          !this.$v.inputValue.numeric ||
          !this.$v.inputValue.minLength ||
          !this.$v.inputValue.maxLength
        ) {
          return this.$t('questionForm.pastYear.errorLengthText');
        } else if (!this.$v.inputValue.between) {
          return this.$t('questionForm.pastYear.errorBetweenText');
        } else if (this.$v.inputValue.$error) {
          return this.$t('questionForm.pastYear.errorText');
        }
      }
      return '';
    }
  },
  watch: {
    responses() {
      if (this.responses[this.formName][this.inputData.id]) {
        this.$v.inputValue.$model = this.responses[this.formName][this.inputData.id];
      }
    }
  },
  created() {
    this.$eventHub.$on(`validateInput_${this.inputData.id}_${this.stepId}`, () => this.validate());
    this.$eventHub.$on(`resetValidationInput_${this.inputData.id}_${this.stepId}`, () =>
      this.reset()
    );

    this.$v.inputValue.$model =
      this.responses[this.formName][this.inputData.id] || this.$v.inputValue.$model;

    if (this.$v.inputValue.$model) {
      this.updateValue(false);
    }
    this.reset();
  },
  destroyed() {
    this.$eventHub.$off(`validateInput_${this.inputData.id}_${this.stepId}`);
    this.$eventHub.$off(`resetValidationInput_${this.inputData.id}_${this.stepId}`);
  },
  methods: {
    validate() {
      this.$v.$touch();
      this.$store.commit('dynamicForms/updateValidation', {
        id: this.inputData.id,
        value: !this.$v.$invalid,
        formName: this.formName
      });
    },
    reset() {
      this.$v.$reset();
    },
    updateValue(emit = true) {
      // this.sendGtmEvent(this.inputData.label)
      const newValue = this.$v.inputValue.$model.length ? this.$v.inputValue.$model : null;

      const updateResponsePayload = {
        id: this.inputData.id,
        value: newValue,
        formName: this.formName
      };

      this.$store.dispatch('dynamicForms/updateResponse', updateResponsePayload);
      if (emit) {
        this.$emit('update:value');
      }
    }
  }
};
</script>
